import React from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Stack,
  Divider,
  Chip,
  Button
} from '@mui/material';
import { CheckCircle2 } from 'lucide-react'; // Added for modern SaaS list styling
import Nav from '../new pages/Nav';
import Footer from '../footer';
import Scrolltotop from '../../Button/Button';

const PlanAndPricing = () => {
  // Original plans data preserved exactly
  const plans = [
    {
      name: 'Free',
      monthlyPrice: 0,
      color: '#a5b4fc',
      popular: false,
      description: 'Great for getting started',
      features: [
        'Create unlimited invoices without any cost',
        'Add and manage an unlimited number of customers',
        'Add, update, and organize your products/services list',
        'Manually record basic income and expense transactions',
      ],
    },
    {
      name: 'Basic',
      monthlyPrice: 1000,
      color: '#c4b5fd',
      popular: true,
      description: 'Best for small teams',
      features: [
        'Create and manage unlimited invoices and expense entries',
        'Track inventory levels and get low stock alerts',
        'Log and monitor your business investments easily',
        'Generate accurate Profit & Loss reports for decision-making',
        'Access 24/7 chat and email support for any issues',
      ],
    },
    {
      name: 'Premium',
      monthlyPrice: 2500,
      color: '#99f6e4',
      popular: false,
      description: 'For growing enterprises',
      features: [
        'Includes all features from the Basic plan',
        'Manage product returns with proper adjustment entries',
        'Get detailed reports for outstanding dues and financial summaries',
        'Analyze customer behavior and product performance with analytics tools',
        'Access to priority support and dedicated onboarding assistance',
      ],
    },
  ];

  return (
    <>
      <Nav />
      
      {/* Modern Fintech Light Background */}
      <Box 
        sx={{ 
          pt: { xs: 12, md: 16 }, // Padding adjusted for Nav
          pb: { xs: 10, md: 14 }, 
          backgroundColor: '#F8FAFC', 
          backgroundImage: 'radial-gradient(at 100% 0%, rgba(79, 70, 229, 0.08) 0px, transparent 50%), radial-gradient(at 0% 100%, rgba(16, 185, 129, 0.05) 0px, transparent 50%)',
          minHeight: '100vh',
          position: 'relative'
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          
          <Chip
             label="Pricing"
             sx={{
                 display: 'flex',
                 width: 'fit-content',
                 margin: '0 auto',
                 mb: 3,
                 backgroundColor: '#EFF6FF',
                 color: '#1D4ED8',
                 fontWeight: 600,
                 borderRadius: '8px',
                 border: '1px solid #BFDBFE',
             }}
          />

          <Typography 
            variant="h2" 
            sx={{
              color: '#0F172A', 
              fontWeight: 800, 
              textAlign: 'center', 
              fontSize: { xs: '2.5rem', sm: '3rem', md: '3.5rem' },
              letterSpacing: '-0.03em',
              mb: 2
            }}
          >
            Plan & Pricing
          </Typography>
          
          <Typography 
            variant="body1" 
            sx={{ 
              color: '#475569', 
              textAlign: 'center', 
              mb: 8, 
              fontSize: '1.125rem',
              maxWidth: '600px',
              mx: 'auto'
            }}
          >
            Simple pricing. No hidden fees. Choose what works best for you.
          </Typography>

          <Grid container spacing={4} justifyContent="center" alignItems="stretch">
            {plans.map((plan, idx) => {
              // Original calculation logic preserved
              const monthly = plan.monthlyPrice;
              const yearly = monthly * 12 * 0.9;
              const originalYear = monthly * 12;

              const formattedMonthly =
                monthly === 0
                  ? 'Free'
                  : `LKR ${monthly.toLocaleString(undefined, { minimumFractionDigits: 2 })} / month`;

              const formattedYearly =
                monthly === 0
                  ? 'Free'
                  : `LKR ${yearly.toLocaleString(undefined, { minimumFractionDigits: 2 })} / year`;

              return (
                <Grid item xs={12} md={4} key={idx} sx={{ display: 'flex' }}>
                  <Card
                    elevation={0}
                    sx={{
                      position: 'relative',
                      overflow: 'visible',
                      borderRadius: '24px',
                      border: plan.popular ? '2px solid #4F46E5' : '1px solid #E2E8F0',
                      boxShadow: plan.popular 
                        ? '0 25px 50px -12px rgba(79, 70, 229, 0.15)' 
                        : '0 10px 25px -5px rgba(0,0,0,0.05)',
                      bgcolor: '#ffffff',
                      height: '100%',
                      width: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-5px)',
                        boxShadow: plan.popular 
                          ? '0 30px 60px -12px rgba(79, 70, 229, 0.25)' 
                          : '0 20px 40px -5px rgba(0,0,0,0.1)',
                      }
                    }}
                  >
                    <CardContent sx={{ flexGrow: 1, p: 4, display: 'flex', flexDirection: 'column' }}>
                      
                      {/* Floating Popular Badge */}
                      {plan.popular && (
                        <Chip
                          label="Most Popular"
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            bgcolor: '#4F46E5',
                            color: '#ffffff',
                            fontWeight: 700,
                            fontSize: '0.75rem',
                            letterSpacing: '0.05em',
                            textTransform: 'uppercase',
                            boxShadow: '0 4px 14px rgba(79, 70, 229, 0.4)',
                          }}
                        />
                      )}

                      <Typography variant="h5" sx={{ color: '#0F172A', fontWeight: 800, mb: 1 }}>
                        {plan.name}
                      </Typography>
                      
                      <Typography variant="body2" sx={{ color: '#64748B', mb: 3 }}>
                        {plan.description}
                      </Typography>

                      {/* Pricing Block */}
                      <Box sx={{ mb: 1, minHeight: '80px' }}>
                        <Typography 
                          variant="h6" 
                          sx={{ 
                            fontWeight: 800, 
                            color: '#0F172A', 
                            fontSize: monthly === 0 ? '3rem' : '1.75rem',
                            lineHeight: 1.2
                          }}
                        >
                          {formattedMonthly}
                        </Typography>

                        {/* Savings Logic */}
                        {monthly > 0 && (
                          <Box sx={{ mt: 1 }}>
                            <Typography
                              variant="body2"
                              sx={{ textDecoration: 'line-through', color: '#94A3B8', display: 'inline-block', mr: 1 }}
                            >
                              LKR {originalYear.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ color: '#10B981', fontWeight: 700, display: 'inline-block', bgcolor: '#ECFDF5', px: 1, py: 0.5, borderRadius: '6px' }}
                            >
                              {formattedYearly} (Save 10%)
                            </Typography>
                          </Box>
                        )}
                      </Box>

                      <Divider sx={{ my: 3, borderColor: '#F1F5F9' }} />

                      {/* Features List */}
                      <Stack spacing={2} sx={{ mb: 4, flexGrow: 1 }}>
                        {plan.features.map((feature, i) => (
                          <Stack direction="row" spacing={1.5} key={i} alignItems="flex-start">
                            <Box sx={{ color: plan.popular ? '#4F46E5' : '#10B981', mt: 0.2 }}>
                              <CheckCircle2 size={20} />
                            </Box>
                            <Typography
                              variant="body2"
                              sx={{ color: '#475569', lineHeight: 1.5, fontSize: '0.9rem' }}
                            >
                              {feature}
                            </Typography>
                          </Stack>
                        ))}
                      </Stack>

                      {/* Modern Call to Action Button */}
                      <Button
                        href={'/payment'}
                        variant={plan.popular ? "contained" : "outlined"}
                        fullWidth
                        size="large"
                        sx={{
                          py: 1.5,
                          borderRadius: '12px',
                          textTransform: 'none',
                          fontWeight: 600,
                          fontSize: '1rem',
                          backgroundColor: plan.popular ? '#4F46E5' : 'transparent',
                          color: plan.popular ? 'white' : '#0F172A',
                          borderColor: plan.popular ? 'none' : '#E2E8F0',
                          '&:hover': {
                            backgroundColor: plan.popular ? '#4338CA' : '#F8FAFC',
                            borderColor: plan.popular ? 'none' : '#CBD5E1',
                          }
                        }}
                      >
                        {monthly === 0 ? 'Get Started for Free' : 'Choose Plan'}
                      </Button>

                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Container>
      </Box>
      <Footer />
      <Scrolltotop/>
    </>
  );
};

export default PlanAndPricing;